import { type QueryKey, useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";

export interface PagedResponse<T> {
  items: T[];
  total: number;
}

export interface UseInfinitePagedListParams<T> {
  queryKey: QueryKey;
  fetchPage: (params: { skip: number; limit: number }) => Promise<PagedResponse<T>>;
  pageSize: number;
  enabled?: boolean;
}

export function useInfinitePagedList<T>({
  queryKey,
  fetchPage,
  pageSize,
  enabled = true,
}: UseInfinitePagedListParams<T>) {
  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => fetchPage({ skip: pageParam * pageSize, limit: pageSize }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.items.length, 0);
      return loaded < lastPage.total ? allPages.length : undefined;
    },
    enabled,
  });

  const items = useMemo<T[]>(
    () => query.data?.pages.flatMap((p) => p.items) ?? [],
    [query.data],
  );
  const total = query.data?.pages[0]?.total ?? 0;

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { rootMargin: "300px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    items,
    total,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: !!query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    sentinelRef,
    query,
  };
}
